import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, PanelLeftOpen, Plus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { BudgetStrip } from '../components/calendar/budget-strip'
import {
  CalendarMobileSlots,
  CalendarMobileTopCenter,
} from '../components/calendar/calendar-mobile-slots'
import { type DraftRange, HourGrid } from '../components/calendar/hour-grid'
import { MonthView } from '../components/calendar/month-view'
import { SollIstStrip } from '../components/calendar/soll-ist-strip'
import { WeekFooter } from '../components/calendar/week-footer'
import { YearView } from '../components/calendar/year-view'
import { CalendarInfoPanel } from '../components/calendar-info-panel'
import { CalendarTimerButton } from '../components/calendar-timer'
import { ErfassenPanel, type ErfassenTab } from '../components/erfassen-panel'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { SegmentedControl } from '../components/ui/tab-nav'
import { type Absence, api, type Meeting, type TimeEntry } from '../lib/api'
import { tiersOf } from '../lib/break-rules'
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  dayKey,
  endOfMonth,
  endOfWeek,
  endOfYear,
  isoWeek,
  isSameDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from '../lib/calendar'
import { calendarMeetingsQuery } from '../lib/calendar-meetings-query'
import {
  metricsForDay,
  sumMetricsUntilToday,
  useActiveBreakRule,
  useBreaksInRange,
  useHolidaysInRange,
  useWorkSchedulesForUser,
} from '../lib/calendar-overlays'
import { invalidateEntryData, invalidateMeetingData } from '../lib/invalidate'
import { notify } from '../lib/notify'
import { periodTitle } from '../lib/period-title'
import { useIsMobile, useShellBars } from '../lib/use-media-query'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/calendar',
  component: CalendarPage,
})

export type ViewMode = 'day' | 'week' | 'month' | 'year'

function CalendarPage() {
  const queryClient = useQueryClient()
  const [view, setView] = useState<ViewMode>('week')
  const [cursor, setCursor] = useState(() => new Date())
  // "View as": admin/leader can pick another manageable user; '' means self.
  const [viewAsUserId, setViewAsUserId] = useState<string>('')
  // When viewing another user, the calendar is read-only by default. An
  // admin/leader can flip this on to fully edit that user's time entries.
  // Always reset to read-only when the viewed user changes (below).
  const [editOther, setEditOther] = useState(false)
  // Unified Erfassen-Panel state — replaces the previous EntryFormDialog +
  // AbsenceFormDialog pair. `defaultTab` opens the panel on a specific tab;
  // when one of entry/absence/meeting is set, the panel locks to that tab in
  // edit-mode. `draft` carries calendar drag-select payload into the Eintrag
  // tab. `dayContext` is the day the panel was opened from — used for
  // pre-filling dates in new entries / absences / meetings.
  const [erfassen, setErfassen] = useState<{
    open: boolean
    defaultTab: ErfassenTab
    entry: TimeEntry | null
    absence: Absence | null
    meeting: Meeting | null
    draft: DraftRange | null
    dayContext: Date | null
  }>({
    open: false,
    defaultTab: 'entry',
    entry: null,
    absence: null,
    meeting: null,
    draft: null,
    dayContext: null,
  })

  const closeErfassen = () =>
    setErfassen({
      open: false,
      defaultTab: 'entry',
      entry: null,
      absence: null,
      meeting: null,
      draft: null,
      dayContext: null,
    })

  // Helpers for opening the panel in different modes — keep the JSX below
  // tidy. All carry `dayContext: cursor` so newly-created items default to
  // the day the user is currently viewing.
  const openEntryCreate = (draft: DraftRange | null = null) =>
    setErfassen({
      open: true,
      defaultTab: 'entry',
      entry: null,
      absence: null,
      meeting: null,
      draft,
      dayContext: cursor,
    })
  const openEntryEdit = (entry: TimeEntry) =>
    setErfassen({
      open: true,
      defaultTab: 'entry',
      entry,
      absence: null,
      meeting: null,
      draft: null,
      dayContext: cursor,
    })

  // Info-Panel state (Anträge / Einträge / Meetings sidebar to the LEFT
  // of the calendar). Persisted to localStorage so the user's choice
  // survives reload.
  const [infoPanelOpen, setInfoPanelOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    // <lg rendert das Panel als Sheet/Slide-in-Overlay — das gespeicherte
    // Open gilt nur für das Desktop-Push-Layout. Mobil startet immer
    // geschlossen, sonst landet der App-Start unerwartet im Overlay.
    if (!window.matchMedia('(min-width: 1024px)').matches) return false
    try {
      return window.localStorage.getItem('calendar-info-panel-open') === 'true'
    } catch {
      return false
    }
  })
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem('calendar-info-panel-open', infoPanelOpen ? 'true' : 'false')
    } catch {
      // ignore
    }
  }, [infoPanelOpen])

  // Mobile: show single day with tabs instead of 7-column week grid.
  const isMobile = useIsMobile()
  // Bars sichtbar = Phone ODER Tablet-Band (T3): gated die Registrierung
  // der Bottom-Slots — NICHT die Layout-Zweige (die bleiben isMobile).
  const barsVisible = useShellBars()
  const [mobileDay, setMobileDay] = useState(() => {
    // Default to today's weekday index (0=Mo .. 6=So)
    const today = new Date()
    return (today.getDay() + 6) % 7
  })

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  const { data: manageableData } = useQuery({
    queryKey: ['users', 'manageable'],
    queryFn: () => api.listManageableUsers(),
    enabled: !!meData?.user,
  })
  const manageableUsers = manageableData?.users ?? []
  const showViewAsPicker = manageableUsers.length > 1
  const viewedUser = viewAsUserId
    ? (manageableUsers.find((u) => u.id === viewAsUserId) ?? null)
    : null
  // For holiday/break-rule filtering we want the *target* user's settings
  // when viewing as someone else; for read-only fall back to session user.
  const overlayUser = viewedUser ?? meData?.user ?? null
  const isViewingOther = !!viewedUser && viewedUser.id !== meData?.user?.id
  // Read-only unless the user explicitly enabled edit-mode for the other user.
  // All time-entry edit affordances gate on this; the backend independently
  // enforces canManageUserData, so this is the UI half of the permission.
  const readOnly = isViewingOther && !editOther
  // Switching the viewed user (or back to own view) always drops edit-mode.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset keyed only on selection change
  useEffect(() => {
    setEditOther(false)
  }, [viewAsUserId])

  const moveEntry = useMutation({
    mutationFn: (vars: { id: string; startTime: Date; endTime: Date }) =>
      api.updateEntry(vars.id, {
        startTime: vars.startTime.toISOString(),
        endTime: vars.endTime.toISOString(),
      }),
    onSuccess: () => {
      invalidateEntryData(queryClient)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // Kopie eines Eintrags (Strg+Drag aufs Ziel / Duplizieren-Menü): Inhalts-
  // felder übernehmen, Meeting-Verknüpfung bewusst NICHT (die Kopie ist ein
  // unabhängiger Arbeitsblock, kein zweiter Meeting-Teilnahme-Nachweis).
  const copyEntry = useMutation({
    mutationFn: (vars: { source: TimeEntry; startTime: Date; endTime: Date }) =>
      api.createEntry({
        description: vars.source.description,
        startTime: vars.startTime.toISOString(),
        endTime: vars.endTime.toISOString(),
        projectId: vars.source.projectId,
        serviceId: vars.source.serviceId,
        billable: vars.source.billable,
        notes: vars.source.notes,
        tagIds: vars.source.tagIds,
        travelTimeToMinutes: vars.source.travelTimeToMinutes,
        travelTimeFromMinutes: vars.source.travelTimeFromMinutes,
        travelCountsAsWork: vars.source.travelCountsAsWork,
        ...(viewAsUserId ? { userId: viewAsUserId } : {}),
      }),
    onSuccess: () => {
      invalidateEntryData(queryClient)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const handleCopyEntry = (id: string, startTime: Date, endTime: Date) => {
    const source = entries.find((e) => e.id === id)
    if (source) copyEntry.mutate({ source, startTime, endTime })
  }
  // Duplizieren: Kopie direkt im Anschluss ans Original, gleiche Dauer
  // (Clockodo-Stil) — der nächste Block beginnt, wo der letzte endete.
  const handleDuplicateEntry = (id: string) => {
    const source = entries.find((e) => e.id === id)
    if (!source?.endTime) return
    const startMs = new Date(source.startTime).getTime()
    const endMs = new Date(source.endTime).getTime()
    copyEntry.mutate({
      source,
      startTime: new Date(endMs),
      endTime: new Date(endMs + (endMs - startMs)),
    })
  }

  const resizeEntry = useMutation({
    mutationFn: (vars: { id: string; startTime?: Date; endTime?: Date }) =>
      api.updateEntry(vars.id, {
        startTime: vars.startTime?.toISOString(),
        endTime: vars.endTime?.toISOString(),
      }),
    onSuccess: () => {
      invalidateEntryData(queryClient)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const moveMeeting = useMutation({
    mutationFn: (vars: { id: string; startTime: Date; endTime: Date }) =>
      api.updateMeeting(vars.id, {
        startTime: vars.startTime.toISOString(),
        endTime: vars.endTime.toISOString(),
      }),
    onSuccess: () => {
      invalidateMeetingData(queryClient)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const resizeMeeting = useMutation({
    mutationFn: (vars: { id: string; startTime?: Date; endTime?: Date }) =>
      api.updateMeeting(vars.id, {
        startTime: vars.startTime?.toISOString(),
        endTime: vars.endTime?.toISOString(),
      }),
    onSuccess: () => {
      invalidateMeetingData(queryClient)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const deleteMeetingMutation = useMutation({
    mutationFn: (id: string) => api.deleteMeeting(id),
    onSuccess: () => {
      invalidateMeetingData(queryClient)
      queryClient.invalidateQueries({ queryKey: ['meetings', 'recent'] })
    },
  })

  const deleteEntry = useMutation({
    mutationFn: (id: string) => api.deleteEntry(id),
    onSuccess: () => {
      invalidateEntryData(queryClient)
      notify.success('Eintrag gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onDeleteWithConfirm = async (id: string) => {
    const ok = await confirm({
      title: 'Eintrag löschen?',
      variant: 'danger',
      confirmLabel: 'Löschen',
    })
    if (ok) deleteEntry.mutate(id)
  }

  const splitEntry = useMutation({
    mutationFn: ({ id, splitAt }: { id: string; splitAt: string }) => api.splitEntry(id, splitAt),
    onSuccess: () => {
      invalidateEntryData(queryClient)
      notify.success('Eintrag geteilt')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // The split point is chosen in-grid (drag the split line); the HourGrid
  // calls this with the confirmed ISO timestamp. No confirm dialog — the
  // in-grid Teilen/Abbrechen controls are the confirmation.
  const onSplitAt = (id: string, splitAt: string) => splitEntry.mutate({ id, splitAt })

  const range = useMemo(() => {
    if (view === 'day') {
      const start = new Date(cursor)
      start.setHours(0, 0, 0, 0)
      const end = new Date(cursor)
      end.setHours(23, 59, 59, 999)
      return { start, end }
    }
    if (view === 'week') return { start: startOfWeek(cursor), end: endOfWeek(cursor) }
    if (view === 'month') return { start: startOfMonth(cursor), end: endOfMonth(cursor) }
    return { start: startOfYear(cursor), end: endOfYear(cursor) }
  }, [view, cursor])

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(cursor), i)),
    [cursor],
  )

  // Stable list of every day in the current view's range. Used by the
  // Soll/Ist strip for month + year (week uses weekDays, day uses [cursor]).
  const rangeDays = useMemo(() => {
    const out: Date[] = []
    let d = new Date(range.start)
    d.setHours(0, 0, 0, 0)
    const end = new Date(range.end)
    end.setHours(0, 0, 0, 0)
    while (d <= end) {
      out.push(new Date(d))
      d = addDays(d, 1)
    }
    return out
  }, [range.start, range.end])

  const { data: entriesData } = useQuery({
    queryKey: [
      'entries',
      'calendar',
      view,
      range.start.toISOString(),
      range.end.toISOString(),
      viewAsUserId,
    ],
    queryFn: () =>
      api.listEntries({
        from: range.start.toISOString(),
        to: range.end.toISOString(),
        userId: viewAsUserId || undefined,
      }),
    enabled: !!meData?.user,
  })

  const { data: absencesData } = useQuery({
    queryKey: [
      'absences',
      'calendar',
      range.start.toISOString(),
      range.end.toISOString(),
      viewAsUserId,
    ],
    queryFn: () =>
      api.listAbsences({
        from: range.start.toISOString(),
        to: range.end.toISOString(),
        userId: viewAsUserId || undefined,
      }),
    enabled: !!meData?.user,
  })

  const { data: projectsData } = useQuery({
    queryKey: ['projects', { archived: false }],
    queryFn: () => api.listProjects(),
    enabled: !!meData?.user,
  })

  const { data: meetingsData } = useQuery(
    calendarMeetingsQuery(meData?.user?.id, viewAsUserId, range.start, range.end),
  )

  // overlayUser drives holiday-group + break-rule filtering so that viewing
  // a team member shows their holidays / break tiers, not the leader's.
  // Target hours stay scoped to the session user for now (no per-user
  // admin-readable endpoint exists).
  const holidays = useHolidaysInRange(
    overlayUser,
    range.start.toISOString(),
    range.end.toISOString(),
  )
  const breaksList = useBreaksInRange(
    meData?.user ?? null,
    range.start.toISOString(),
    range.end.toISOString(),
    viewAsUserId || undefined,
  )
  const breakRule = useActiveBreakRule(overlayUser)
  const breakTiers = tiersOf(breakRule)
  const targetHoursMap = useWorkSchedulesForUser(meData?.user ?? null, viewAsUserId || undefined)

  // Kein actualFlags mehr: Meetings gaten pro Meeting (countsAsWork),
  // Reisezeit pro Eintrag (travelCountsAsWork) — metricsForDay braucht
  // keine globalen Schalter, alle Kalender-Anzeigen zählen identisch.

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  const entries = entriesData?.entries ?? []
  const absences = absencesData?.absences ?? []
  const projects = projectsData?.projects ?? []
  const projectMap = new Map(projects.map((p) => [p.id, p]))
  const meetings = meetingsData?.meetings ?? []

  // Die vom aktiven View abgedeckten Tage — geteilt vom SollIstStrip (Header/
  // Sheet) und der Ampel-Aggregation darunter.
  const sollIstDays = view === 'day' ? [cursor] : view === 'week' ? weekDays : rangeDays

  // Aggregiertes Soll/Ist für den Ampelpunkt der mobilen Top-Mitte
  // (CalendarMobileTopCenter). NUR auf dem Phone berechnet (dort ersetzt der
  // Punkt den Header-SollIstStrip) — es ist derselbe metricsForDay-Lauf, den
  // der Strip sonst macht, also kein Mehraufwand gegenüber dem Desktop-Kopf.
  const mobileSollIst = { actual: 0, target: 0, arbzg: false }
  if (isMobile) {
    // Zukunfts-Cap wie im Kopf-Strip: nur Tage bis heute (Ticket „tagesaktuell“).
    const metricsByDay = sollIstDays.map((d) =>
      metricsForDay(
        d,
        entries,
        breaksList,
        breakTiers,
        holidays,
        absences,
        targetHoursMap,
        meetings,
      ),
    )
    const sums = sumMetricsUntilToday(sollIstDays, metricsByDay)
    mobileSollIst.target = sums.targetMinutes
    mobileSollIst.actual = sums.totalActualMinutes
    mobileSollIst.arbzg = sums.arbzgViolation
  }

  // Open the unified Erfassen-Panel on the Meeting tab, pre-filled with the
  // clicked meeting (edit-mode locks the tab).
  const openMeetingEdit = (meeting: Meeting) =>
    setErfassen({
      open: true,
      defaultTab: 'meeting',
      entry: null,
      absence: null,
      meeting,
      draft: null,
      dayContext: cursor,
    })

  const onMeetingDelete = async (meeting: Meeting) => {
    const ok = await confirm({
      title: 'Meeting löschen?',
      description: `${meeting.title} – ${new Date(meeting.startTime).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}`,
      variant: 'danger',
      confirmLabel: 'Löschen',
    })
    if (ok) deleteMeetingMutation.mutate(meeting.id)
  }

  const onMeetingJoin = async (meeting: Meeting) => {
    try {
      const r = await api.getMeetingJoinUrl(meeting.id)
      const url = r.joinUrl ?? meeting.onlineUrl
      if (!url) {
        // No managed Jitsi room AND no BYO URL → nothing to join.
        return
      }
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch {
      // Fallback to BYO URL if the managed-join endpoint errors out.
      if (meeting.onlineUrl) window.open(meeting.onlineUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const onPrev = () => {
    if (view === 'day') setCursor(addDays(cursor, -1))
    else if (view === 'week') setCursor(addWeeks(cursor, -1))
    else if (view === 'month') setCursor(addMonths(cursor, -1))
    else setCursor(addYears(cursor, -1))
  }
  const onNext = () => {
    if (view === 'day') setCursor(addDays(cursor, 1))
    else if (view === 'week') setCursor(addWeeks(cursor, 1))
    else if (view === 'month') setCursor(addMonths(cursor, 1))
    else setCursor(addYears(cursor, 1))
  }

  const title = (() => {
    if (view === 'day') {
      return `${cursor.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'short' })} · KW ${isoWeek(cursor)}`
    }
    if (view === 'week') {
      // Kanonisches Format (lib/period-title): „KW 28 · 06.–12. Juli 2026" —
      // identisch zu team-planning/disposition.
      return periodTitle(startOfWeek(cursor), endOfWeek(cursor))
    }
    if (view === 'month')
      return cursor.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
    return String(cursor.getFullYear())
  })()

  return (
    <>
      <div
        // ≥640: tight per-breakpoint height that subtracts ONLY the chrome
        // the root main container imposes. 100dvh is the dynamic viewport
        // unit — accounts for the iOS browser bar collapsing on scroll, so
        // we don't reserve a phantom 80px gap.
        //   640–1023 (Tablet-Band, T2 „Rail + Bar"): Bottom-Bar h-14 (56px)
        //        + main py-3 (24px) = 80px. Die alte Topbar (69px + Safe-
        //        Top) ist gelöscht. Dazu env(safe-area-inset-bottom): die
        //        Bar wächst in der Standalone-PWA per Inline-Padding um
        //        genau diesen Wert (mobile-shell-bottom.tsx), im
        //        Browser-Tab ist env() = 0.
        //   lg+ (≥1024, keine Bar): main py-4 (32px) — byte-identisch.
        // Fixed (not min-) height so the inner flex-1 children — the
        // calendar grid AND the CalendarInfoPanel tab body — get a bounded
        // height that lets their overflow-y-auto containers actually
        // scroll. Without this the parent grows with the longest child
        // and nothing ever overflows.
        className={cn(
          'flex flex-col animate-fade-in gap-2',
          isMobile
            ? // Phone (<640): h-full füllt den Page-Container (in __root auf
              // Scroller-Höhe geklemmt) exakt zwischen den Shell-Bars — der
              // Shell-Scroller klemmt, die alte dvh-Rechnung (Topbar + fixe
              // CalendarBottomBar) ist seit der W4-Konvergenz eine falsche
              // Referenz. Der WeekFooter endet damit konstruktionsbedingt
              // über der Bottom-Bar (Muster kanban/chat, W1).
              'h-full min-h-0'
            : // ≥640: Band-einheitlich 80px + Bar-Inset (Rechnung oben),
              // lg stellt den Desktop-Wert wieder her (kein md:-Zweig mehr —
              // das Band 640–1023 ist seit T2 uniform).
              'h-[calc(100dvh-80px-var(--app-safe-bottom))] lg:h-[calc(100dvh-32px)]',
        )}
      >
        {/* <640: ◀ · Titel● · ▶ + Zeitraum-Sheet portalen in die Top-Mitte
            der Shell (🔥/🔔 liefert MobileShellTop) — kein eigener Kopf mehr.
            ≥640 (Tablet+Desktop) rendert unverändert der einzeilige
            sm:contents-Kopf aus PR #27. */}
        {isMobile ? (
          <CalendarMobileTopCenter
            title={title}
            onPrev={onPrev}
            onNext={onNext}
            onToday={() => setCursor(new Date())}
            actualMin={mobileSollIst.actual}
            targetMin={mobileSollIst.target}
            hasArbzgWarning={mobileSollIst.arbzg}
            sollIstDetail={
              <SollIstStrip
                days={sollIstDays}
                entries={entries}
                breaks={breaksList}
                absences={absences}
                holidays={holidays}
                breakTiers={breakTiers}
                targetHours={targetHoursMap}
                meetings={meetings}
              />
            }
          />
        ) : (
          <div className="pb-3 border-b border-border-subtle">
            {/* Desktop/Tablet (≥sm): eine Zeile, flex-wrap auf ALLEN Breiten
              — bei breiten Screens passt alles in die Zeile (Wrap = No-op,
              Optik identisch), im engen lg-Band (z.B. 1024×768) brechen die
              rechten Controls kontrolliert um statt zu clippen (vorher
              lg:flex-nowrap → „+ Erfassen" abgeschnitten). Tablet-Band
              640–767 (T3 geprüft, Content ≈551px nach Rail+Padding):
              Standard-Toolbar wrappt sauber 2-zeilig (Zeile 1 trägt Titel
              truncatend + Strip shrink-0, Zeile 2 Switcher+Timer+Erfassen
              ≈420px); nur Admin MIT View-As-Select (~+180px) bricht unter
              ~700px kontrolliert 3-zeilig — Wrap, nie Clip. | Mobil (<sm):
              zwei bewusste 44px-Zeilen. Die beiden Gruppen-Divs lösen
              sich ab sm via display:contents auf — ihre Kinder nehmen dann
              wieder direkt am äußeren Flex teil, die Desktop-Zeile bleibt
              dadurch unverändert. Mobil-Zusätze an den Kindern werden ab sm
              explizit auf die Defaults zurückgesetzt (sm:flex-initial usw.)
              — max-sm: steht nicht zur Verfügung, weil der raw-`touch`-
              Screen in tailwind.config.js die max-*-Varianten deaktiviert. */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Zeile 1 (mobil): Panel-Toggle · ◀ · Heute · ▶ · Titel */}
              <div className="flex w-full min-h-11 items-center gap-2 sm:contents">
                <Button
                  variant={infoPanelOpen ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setInfoPanelOpen((v) => !v)}
                  aria-pressed={infoPanelOpen}
                  aria-label="Anträge / Einträge / Meetings"
                  title="Anträge / Einträge / Meetings"
                >
                  <PanelLeftOpen className="h-4 w-4" />
                  <span className="hidden sm:inline">Liste</span>
                </Button>
                <Button variant="ghost" size="icon" onClick={onPrev} aria-label="Zurück">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setCursor(new Date())}>
                  Heute
                </Button>
                <Button variant="ghost" size="icon" onClick={onNext} aria-label="Vor">
                  <ChevronRight className="h-4 w-4" />
                </Button>
                {/* Mobil füllt der Titel den Rest von Zeile 1 (rechtsbündig);
                  in Monat/Jahr sind die Titel kurz („Juli 2026") und teilen
                  sich die Zeile mit dem SollIstStrip — der Titel truncated,
                  der Strip bleibt vollständig. */}
                <p
                  className={cn(
                    'text-xs sm:text-sm font-medium text-text-primary truncate',
                    // sm:flex-initial/sm:text-left = Flex-/Text-Defaults, ≥sm
                    // rendert exakt wie vorher.
                    'flex-1 min-w-0 text-right sm:flex-initial sm:text-left',
                  )}
                >
                  {title}
                </p>
                <SollIstStrip
                  compact
                  days={view === 'day' ? [cursor] : view === 'week' ? weekDays : rangeDays}
                  entries={entries}
                  breaks={breaksList}
                  absences={absences}
                  holidays={holidays}
                  breakTiers={breakTiers}
                  targetHours={targetHoursMap}
                  meetings={meetings}
                  // Tag/Woche mobil: WeekFooter/Tages-Summen-Karte zeigt unten
                  // dieselben Zahlen — Strip kostet hier nur Kopfhöhe. In
                  // Monat/Jahr gibt es keinen Footer → Strip bleibt und rückt
                  // rechtsbündig in Zeile 1 (statt des Datums-Langtexts).
                  className={cn(
                    view === 'month' || view === 'year'
                      ? // Mobil rechtsbündig in Zeile 1; darf schrumpfen und
                        // schneidet dann rechts sauber ab (Badge ist <sm eh
                        // ausgeblendet, die Ist/Soll-Zahl bleibt lesbar).
                        'min-w-0 shrink overflow-hidden sm:shrink-0'
                      : 'hidden sm:inline-flex sm:shrink-0',
                  )}
                />
              </div>
              <div className="hidden lg:block flex-1" />
              {/* Zeile 2 (mobil): (View-As) · Ansichts-Umschalter · Stoppuhr · Erfassen.
                flex-wrap nur für den Admin-Sonderfall View-As + laufender
                Timer — dann bricht kontrolliert diese Zeile, nie Zeile 1. */}
              <div className="flex w-full min-h-11 flex-wrap items-center gap-2 sm:contents">
                {showViewAsPicker && (
                  <select
                    value={viewAsUserId}
                    onChange={(e) => setViewAsUserId(e.target.value)}
                    className="h-8 rounded-md border border-border-default bg-bg-overlay px-2 text-xs text-text-primary shrink-0 [color-scheme:dark] focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30"
                    title="Als anderen Mitarbeiter anzeigen"
                  >
                    <option value="">— Eigene Ansicht —</option>
                    {manageableUsers
                      .filter((u) => u.id !== meData?.user?.id)
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                  </select>
                )}
                {/* grow (nicht flex-1!): flex-basis bleibt auto, damit der
                  Admin-Sonderfall View-As + laufender Timer kontrolliert in
                  die nächste Zeile umbricht statt den Switcher zu quetschen;
                  min-w-0 erlaubt Rest-Schrumpfen. ≥sm: Defaults (grow-0) bzw.
                  min-w-fit ≈ bisheriges min-width:auto bei nowrap-Inhalt. */}
                <SegmentedControl
                  value={view}
                  onChange={setView}
                  options={VIEW_OPTIONS}
                  className="grow min-w-0 sm:grow-0 sm:min-w-fit"
                />
                {!isViewingOther && <CalendarTimerButton />}
                <Button
                  size="sm"
                  disabled={readOnly}
                  onClick={() =>
                    setErfassen({
                      open: true,
                      defaultTab: 'entry',
                      entry: null,
                      absence: null,
                      meeting: null,
                      draft: null,
                      dayContext: cursor,
                    })
                  }
                  title={readOnly ? 'Im Lesemodus deaktiviert' : 'Erfassen'}
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline"> Erfassen</span>
                </Button>
              </div>
            </div>
          </div>
        )}

        <BudgetStrip cursorISO={dayKey(cursor)} />

        {isViewingOther && viewedUser && (
          <div
            className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-4 py-2 text-sm ${
              editOther
                ? 'border-warning/40 bg-warning/5 text-text-primary'
                : 'border-info/30 bg-info/5 text-text-secondary'
            }`}
          >
            <span className="min-w-0">
              Du betrachtest die Ansicht von <strong>{viewedUser.name}</strong>.{' '}
              {editOther ? (
                <>
                  <strong>Bearbeiten aktiv</strong> — Einträge wirken direkt auf diesen Mitarbeiter.
                </>
              ) : (
                'Lesemodus.'
              )}
            </span>
            <label className="flex shrink-0 cursor-pointer select-none items-center gap-2 text-xs">
              <span
                className={`whitespace-nowrap ${editOther ? 'text-text-secondary' : 'text-text-primary'}`}
              >
                Lesen
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={editOther}
                aria-label="Bearbeiten-Modus für diesen Mitarbeiter"
                onClick={() => setEditOther((v) => !v)}
                className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
                  editOther ? 'bg-warning' : 'bg-bg-overlay border border-border-default'
                }`}
              >
                <span
                  className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                    editOther ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
              <span
                className={`whitespace-nowrap ${editOther ? 'text-text-primary' : 'text-text-secondary'}`}
              >
                Bearbeiten
              </span>
            </label>
          </div>
        )}

        {meetingsData?.accessDenied && (
          <div
            role="status"
            className="shrink-0 rounded-lg border border-warning/40 bg-warning/5 px-4 py-2 text-sm text-text-primary"
          >
            Du hast keinen Zugriff auf diese Meetings. Sie werden nicht angezeigt.
          </div>
        )}

        <div className="flex-1 flex flex-row gap-4 min-h-0">
          {infoPanelOpen && (
            <CalendarInfoPanel
              open={infoPanelOpen}
              onClose={() => setInfoPanelOpen(false)}
              className="lg:w-[380px] xl:w-[420px] lg:shrink-0"
              targetUserId={isViewingOther ? viewAsUserId : undefined}
              readOnly={readOnly}
            />
          )}
          <div className="flex-1 min-w-0 flex flex-col">
            {view === 'day' && (
              <HourGrid
                readOnly={readOnly}
                days={[cursor]}
                entries={entries}
                projects={projectMap}
                holidays={holidays}
                absences={absences}
                meetings={meetings}
                onMeetingClick={openMeetingEdit}
                onMeetingDelete={onMeetingDelete}
                onMeetingJoin={onMeetingJoin}
                onMeetingMove={
                  isViewingOther
                    ? undefined
                    : (id, startTime, endTime) => moveMeeting.mutate({ id, startTime, endTime })
                }
                onMeetingResize={
                  isViewingOther ? undefined : (id, range) => resizeMeeting.mutate({ id, ...range })
                }
                onEntryClick={readOnly ? () => {} : (entry) => openEntryEdit(entry)}
                onCreate={readOnly ? () => {} : (draft) => openEntryCreate(draft)}
                onCopy={readOnly ? undefined : handleCopyEntry}
                onDuplicate={readOnly ? undefined : handleDuplicateEntry}
                onMove={
                  readOnly
                    ? () => {}
                    : (id, startTime, endTime) => moveEntry.mutate({ id, startTime, endTime })
                }
                onResize={readOnly ? () => {} : (id, range) => resizeEntry.mutate({ id, ...range })}
                onDelete={readOnly ? () => {} : onDeleteWithConfirm}
                onSplit={readOnly ? () => {} : onSplitAt}
                footer={
                  <WeekFooter
                    days={[cursor]}
                    entries={entries}
                    breaks={breaksList}
                    absences={absences}
                    holidays={holidays}
                    breakTiers={breakTiers}
                    targetHours={targetHoursMap}
                    meetings={meetings}
                  />
                }
              />
            )}
            {view === 'week' && (
              <>
                {/* Mobile day tabs — swipe between days. Rein JS-gated
                    (isMobile deckt hoch + quer) — das alte sm:hidden hätte
                    die Tabs im Querformat (Breite ≥640) weggeblendet. */}
                {isMobile && (
                  <div className="flex border-b border-border-default -mt-3">
                    {weekDays.map((d, i) => {
                      const isToday = isSameDay(d, new Date())
                      const isActive = i === mobileDay
                      return (
                        <button
                          key={d.toISOString()}
                          type="button"
                          onClick={() => setMobileDay(i)}
                          className={cn(
                            'flex-1 py-2 text-center text-xs transition-colors',
                            // Selektion füllt orange (Pill unten), Heute ist
                            // nur ein Ring — beide Zustände bleiben auf einen
                            // Blick unterscheidbar (D12).
                            isActive
                              ? 'border-b-2 border-accent text-accent font-medium'
                              : 'text-text-secondary',
                          )}
                        >
                          <div>{['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'][i]}</div>
                          <div
                            className={cn(
                              'mx-auto mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-sm font-medium',
                              isActive && 'bg-accent text-bg-base',
                              isToday && !isActive && 'ring-1 ring-accent text-accent',
                            )}
                          >
                            {d.getDate()}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}

                {/* Desktop: all 7 columns */}
                {!isMobile && (
                  <HourGrid
                    readOnly={readOnly}
                    days={weekDays}
                    entries={entries}
                    projects={projectMap}
                    holidays={holidays}
                    absences={absences}
                    meetings={meetings}
                    onMeetingClick={openMeetingEdit}
                    onMeetingDelete={onMeetingDelete}
                    onMeetingJoin={onMeetingJoin}
                    onMeetingMove={
                      isViewingOther
                        ? undefined
                        : (id, startTime, endTime) => moveMeeting.mutate({ id, startTime, endTime })
                    }
                    onMeetingResize={
                      isViewingOther
                        ? undefined
                        : (id, range) => resizeMeeting.mutate({ id, ...range })
                    }
                    onEntryClick={readOnly ? () => {} : (entry) => openEntryEdit(entry)}
                    onCreate={readOnly ? () => {} : (draft) => openEntryCreate(draft)}
                    onCopy={readOnly ? undefined : handleCopyEntry}
                    onDuplicate={readOnly ? undefined : handleDuplicateEntry}
                    onMove={
                      readOnly
                        ? () => {}
                        : (id, startTime, endTime) => moveEntry.mutate({ id, startTime, endTime })
                    }
                    onResize={
                      readOnly ? () => {} : (id, range) => resizeEntry.mutate({ id, ...range })
                    }
                    onDelete={readOnly ? () => {} : onDeleteWithConfirm}
                    onSplit={readOnly ? () => {} : onSplitAt}
                    footer={
                      <WeekFooter
                        days={weekDays}
                        entries={entries}
                        breaks={breaksList}
                        absences={absences}
                        holidays={holidays}
                        breakTiers={breakTiers}
                        targetHours={targetHoursMap}
                        meetings={meetings}
                      />
                    }
                  />
                )}

                {/* Mobile: single day column */}
                {isMobile && (
                  <HourGrid
                    // Die Tages-Tabs direkt darüber zeigen Wochentag + Datum
                    // bereits — der Spaltenkopf wäre eine doppelte
                    // Tagesanzeige und kostet ~50px Höhe (A10).
                    hideDayHeader
                    readOnly={readOnly}
                    days={[weekDays[mobileDay]]}
                    entries={entries}
                    projects={projectMap}
                    holidays={holidays}
                    absences={absences}
                    meetings={meetings}
                    onMeetingClick={openMeetingEdit}
                    onMeetingDelete={onMeetingDelete}
                    onMeetingJoin={onMeetingJoin}
                    onMeetingMove={
                      isViewingOther
                        ? undefined
                        : (id, startTime, endTime) => moveMeeting.mutate({ id, startTime, endTime })
                    }
                    onMeetingResize={
                      isViewingOther
                        ? undefined
                        : (id, range) => resizeMeeting.mutate({ id, ...range })
                    }
                    onEntryClick={readOnly ? () => {} : (entry) => openEntryEdit(entry)}
                    onCreate={readOnly ? () => {} : (draft) => openEntryCreate(draft)}
                    onCopy={readOnly ? undefined : handleCopyEntry}
                    onDuplicate={readOnly ? undefined : handleDuplicateEntry}
                    onMove={
                      readOnly
                        ? () => {}
                        : (id, startTime, endTime) => moveEntry.mutate({ id, startTime, endTime })
                    }
                    onResize={
                      readOnly ? () => {} : (id, range) => resizeEntry.mutate({ id, ...range })
                    }
                    onDelete={readOnly ? () => {} : onDeleteWithConfirm}
                    onSplit={readOnly ? () => {} : onSplitAt}
                    footer={
                      <WeekFooter
                        days={weekDays}
                        entries={entries}
                        breaks={breaksList}
                        absences={absences}
                        holidays={holidays}
                        breakTiers={breakTiers}
                        targetHours={targetHoursMap}
                        meetings={meetings}
                        selectedDay={weekDays[mobileDay]}
                      />
                    }
                  />
                )}
              </>
            )}
            {view === 'month' && (
              // Scroll lebt im View-Container, nicht im MAIN — Toolbar und
              // Umschalter bleiben stehen wie in der Wochenansicht (A12).
              // pb-4 statt pb-24: der Container endet über der Shell-
              // Bottom-Bar (h-full-Klemme), die alte FAB-Clearance
              // entfällt — nur noch etwas Atemluft am Scroll-Ende.
              <div className="flex-1 min-h-0 overflow-y-auto pb-4 sm:pb-0">
                <MonthView
                  date={cursor}
                  entries={entries}
                  absences={absences}
                  breaks={breaksList}
                  breakTiers={breakTiers}
                  holidays={holidays}
                  targetHours={targetHoursMap}
                  meetings={meetings}
                  onDayClick={(d) => {
                    setCursor(d)
                    setView('day')
                  }}
                />
              </div>
            )}
            {view === 'year' && (
              // Wie beim Monat: Jahres-Grid scrollt im eigenen Container,
              // die Toolbar scrollt nicht mehr aus dem Bild (A12).
              // pb-4 statt pb-24: FAB-Clearance entfällt (s. Monat).
              <div className="flex-1 min-h-0 overflow-y-auto pb-4 sm:pb-0">
                <YearView
                  date={cursor}
                  entries={entries}
                  absences={absences}
                  holidays={holidays}
                  breaks={breaksList}
                  breakTiers={breakTiers}
                  targetHours={targetHoursMap}
                  meetings={meetings}
                  onDayClick={(d) => {
                    setCursor(d)
                    setView('day')
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom-Slots (Liste · Ansicht · View-As · Stoppuhr · ＋) — portalen
          in die generische Shell-Bottom-Bar; das ☰ liefert die Shell als
          fixen Slot 1. Gate = barsVisible (Phone ODER Tablet-Band, T3):
          im Band existieren Toolbar UND Bar-Slots bewusst doppelt
          (Daumen-Redundanz) — identische Handler/States (setInfoPanelOpen,
          setView, setViewAsUserId, openEntryCreate; Toolbar-„+ Erfassen"
          ruft setErfassen mit denselben Defaults). useRunningEntry teilt
          den queryKey mit CalendarTimerButton → kein Doppel-Fetch; ≥1024
          hält das Gate den 1s-Tick weiter vom Desktop fern. */}
      {barsVisible && (
        <CalendarMobileSlots
          view={view}
          onViewChange={setView}
          infoPanelOpen={infoPanelOpen}
          onToggleInfoPanel={() => setInfoPanelOpen((v) => !v)}
          showViewAsPicker={showViewAsPicker}
          manageableUsers={manageableUsers}
          viewAsUserId={viewAsUserId}
          onViewAsChange={setViewAsUserId}
          selfUserId={meData.user.id}
          onErfassen={() => openEntryCreate()}
          readOnly={readOnly}
          isViewingOther={isViewingOther}
        />
      )}

      <ErfassenPanel
        open={erfassen.open}
        onClose={closeErfassen}
        defaultTab={erfassen.defaultTab}
        entry={erfassen.entry}
        absence={erfassen.absence}
        meeting={erfassen.meeting}
        draft={erfassen.draft}
        dayContext={erfassen.dayContext ?? undefined}
        myId={meData.user.id}
        targetUserId={isViewingOther && editOther ? viewAsUserId : undefined}
      />
    </>
  )
}

const VIEW_OPTIONS: readonly { id: ViewMode; label: string }[] = [
  { id: 'day', label: 'Tag' },
  { id: 'week', label: 'Woche' },
  { id: 'month', label: 'Monat' },
  { id: 'year', label: 'Jahr' },
]
